import { useState } from 'react';
import { useActivity, useActivitySummary } from '../lib/queries';
import type { ActivityEntry } from '../data/types';
import { Chip, Empty, Loading, Tile } from '../components/ui';

/* Every area the server may record. The type is exhaustive on purpose: a new
   one added server-side then fails to compile here rather than rendering an
   empty pill, which is how `auth` went unlabelled. */
const AREAS: Record<ActivityEntry['area'], string> = {
  station: 'Stations', charger: 'Chargers', agent: 'Agents', release: 'Releases',
  access: 'Access', money: 'Money', settings: 'Settings', dispute: 'Disputes',
  auth: 'Sign-ins', driver: 'Drivers',
};

type Area = ActivityEntry['area'];

export function Activity() {
  const [area, setArea] = useState<'all' | Area>('all');
  const [q, setQ] = useState('');
  /* Filtering happens on the server so the counts and the rows come from the
     same place — a log filtered in the browser starts lying the moment it is
     longer than one page. */
  const { data, isLoading, error } = useActivity({ area, q });
  const { data: summary } = useActivitySummary();

  if (isLoading) return <Loading />;
  if (error || !data) return <Empty title="The activity log is not available." />;

  const counts = Object.fromEntries(
    (summary?.areas ?? []).map((a) => [a.area, a.n]),
  ) as Partial<Record<Area, number>>;
  const network = (counts.station ?? 0) + (counts.charger ?? 0);

  return (
    <>
      <div className="tiles">
        <Tile k="Entries" v={summary?.total ?? data.length} d="append-only" />
        <Tile k="Last 24h" v={summary?.day ?? 0} d="changes made" />
        <Tile k="Network" v={network} d="charge point changes" />
        <Tile k="Releases" v={counts.release ?? 0} d="app changes" />
      </div>

      <div className="toolbar">
        <div className="chips">
          <Chip
            label={<>All <span className="mono">{summary?.total ?? 0}</span></>}
            selected={area === 'all'}
            onClick={() => setArea('all')}
          />
          {(Object.keys(AREAS) as Area[]).filter((k) => (counts[k] ?? 0) > 0).map((k) => (
            <Chip
              key={k}
              selected={area === k}
              onClick={() => setArea(k)}
              label={<>{AREAS[k]} <span className="mono">{counts[k]}</span></>}
            />
          ))}
        </div>
        <input
          className="field" style={{ width: 'auto', minWidth: 200 }}
          placeholder="Search actor, action or target"
          value={q} onChange={(e) => setQ(e.target.value)}
          aria-label="Search the activity log"
        />
      </div>

      {!data.length ? (
        <Empty
          title={summary?.total ? 'Nothing logged under this filter.' : 'Nothing logged yet.'}
          hint={summary?.total ? undefined
            : 'Entries appear the moment someone blocks a charge point, decides on an agent, or changes a release.'}
        />
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr><th>When</th><th>Who</th><th>Area</th><th>Action</th><th>Target</th><th>Detail</th></tr>
            </thead>
            <tbody>
              {data.map((e) => (
                <tr key={e.id}>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                    {new Date(e.at).toLocaleString(undefined, {
                      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                    })}
                  </td>
                  <td>
                    <div className="li-title">{e.actor}</div>
                    <div className="li-sub">{e.role}</div>
                  </td>
                  <td><span className="pill">{AREAS[e.area]}</span></td>
                  <td>{e.action}</td>
                  <td>{e.target || <span className="faint">—</span>}</td>
                  <td className="li-sub">{e.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="li-sub" style={{ marginTop: 9 }}>
        Newest first · the log is append-only and cannot be edited from the console.
      </div>
    </>
  );
}
